import { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { TutorialsCardData } from './TutorialsCard';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Node, mergeAttributes, type CommandProps } from '@tiptap/core';

// Video extension (copied from HelpCenterEditModal)
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      setVideo: (attrs: {
        src: string;
        title?: string;
        controls?: boolean;
        autoplay?: boolean;
        loop?: boolean;
        muted?: boolean;
        class?: string;
      }) => ReturnType;
    };
    loomVideo: {
      setLoomVideo: (attrs: {
        src: string;
        title?: string;
        class?: string;
      }) => ReturnType;
    };
  }
}

const Video = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  inline: false,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
      autoplay: { default: false },
      loop: { default: false },
      muted: { default: false },
      title: { default: null },
      class: { default: 'max-w-full w-full rounded-md' },
    };
  },
  parseHTML() {
    return [{ tag: 'video' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(HTMLAttributes, { controls: true })];
  },
  addCommands() {
    return {
      setVideo:
        (attrs: {
          src: string;
          title?: string;
          controls?: boolean;
          autoplay?: boolean;
          loop?: boolean;
          muted?: boolean;
          class?: string;
        }) =>
        ({ chain }: CommandProps) => {
          if (!attrs?.src) return false;
          return chain()
            .insertContent({
              type: this.name,
              attrs,
            })
            .run();
        },
    };
  },
});

const LoomVideo = Node.create({
  name: 'loomVideo',
  group: 'block',
  atom: true,
  inline: false,
  draggable: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: null },
      title: { default: null },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-loom-video]',
        getAttrs: (dom) => {
          const iframe = dom.querySelector('iframe');
          const src = iframe?.getAttribute('src');
          return src && src.includes('loom.com') ? { src } : null;
        }
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.src;
    if (src && src.includes('loom.com')) {
      // Convert share URL to embed URL
      let embedSrc = src;
      if (src.includes('/share/')) {
        embedSrc = src.replace('/share/', '/embed/');
      }

      return ['div', {
        'data-loom-video': '',
        'class': 'loom-video-wrapper',
        'style': 'position: relative; padding-bottom: 62.5%; height: 0; margin: 1rem 0;'
      },
        ['iframe', {
          src: embedSrc,
          frameborder: '0',
          webkitallowfullscreen: 'true',
          mozallowfullscreen: 'true',
          allowfullscreen: 'true',
          style: 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;'
        }]
      ];
    }
    return ['div', { class: 'text-red-500' }, 'Invalid Loom URL'];
  },
  addCommands() {
    return {
      setLoomVideo:
        (attrs: { src: string; title?: string }) =>
        ({ chain }: CommandProps) => {
          if (!attrs?.src || !attrs.src.includes('loom.com')) return false;
          return chain()
            .insertContent({
              type: this.name,
              attrs: { src: attrs.src, title: attrs.title },
            })
            .run();
        },
    };
  },
});

interface TutorialsViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: TutorialsCardData | null;
  mode: 'admin' | 'client';
}

function TutorialsViewModal({ isOpen, onClose, card, mode }: TutorialsViewModalProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        heading: false,
      }),
      Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
      Link.configure({
        autolink: true,
        openOnClick: true,
        linkOnPaste: true,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-md',
        },
      }),
      Video,
      LoomVideo,
      Table.configure({
        resizable: true,
        lastColumnResizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '',
    editable: false,
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[120px] px-0 py-0 overflow-y-auto dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-a:text-primary prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground prose-blockquote:text-foreground prose-ol:text-foreground prose-ul:text-foreground prose-li:marker:text-foreground prose-table:text-foreground prose-th:text-foreground prose-td:text-foreground tiptap-content text-foreground',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (!card?.content) {
      editor.commands.setContent('<p class="text-sm text-muted-foreground">No content available</p>');
      return;
    }
    editor.commands.setContent(card.content);
  }, [editor, card?.content]);

  if (!card) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl pr-4">{card.title}</DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                {mode === 'admin' && !card.is_published && (
                  <Badge variant="secondary">
                    Draft
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground">
                  Updated {new Date(card.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="pr-6 py-4">
            <EditorContent editor={editor} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TutorialsViewModal;